import {
  ExclamationCircleOutlined,
  FastBackwardOutlined,
  FileTextOutlined,
  LoadingOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  RedoOutlined,
  UndoOutlined
} from "@ant-design/icons"
import { Button, Slider } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"

import { reportUnexpectedFailure } from "../../observability/handled-errors.ts"
import { cn } from "../../utils/cn.ts"
import { createAudioProgressReporter } from "./education-audio-progress.ts"

export type AudioResult =
  | { status: "ready"; url: string; duration_ms: number }
  | { status: "queued" | "synthesizing" }
  | { status: "failed" }

export interface EducationAudioPlayerLabels {
  play: string
  pause: string
  back15: string
  restart: string
  speed: string
  preparing: string
  audioReady: string
  /** Shown instead of `failed` when the asset finished synthesizing fine but
   *  the browser's autoplay policy refused the automatic play-once-ready
   *  attempt — the reader only needs to press Play themselves, not retry a
   *  synthesis that never actually failed. */
  autoplayBlocked: string
  failed: string
  retry: string
  transcriptShow: string
  transcriptHide: string
  /** The visible text on the back-15-seconds button. */
  back15Short: string
  /** The seek bar's accessible name. */
  seek: string
  /** The visible seconds count while preparing. */
  elapsedSeconds: (seconds: number) => string
}

interface EducationAudioPlayerProps {
  fetchAudio: () => Promise<AudioResult>
  retryAudio?: () => Promise<void>
  initialPositionMs?: number
  onProgress?: (positionMs: number, completed: boolean) => void
  /** Fires once per `<audio>` element's own `play` event — including a
   *  resume after pause, not just the very first press. A caller wanting
   *  "first play only" (e.g. an `audio_started` analytics beacon) tracks
   *  that itself; this stays a plain passthrough so it costs existing
   *  callers nothing. */
  onPlay?: () => void
  transcript: React.ReactNode
  /** Shows a sticky bottom mini-player below `md` once playback has started
   * — the "listen while driving" surface stays reachable while the reader
   * scrolls the article. */
  compactOnMobile?: boolean
  /** Shown on the lock screen / car display while playing. */
  title?: string
  labels: EducationAudioPlayerLabels
}

type PlayerState = "idle" | "preparing" | "ready" | "failed"

const POLL_INTERVAL_MS = 2_000
/** A synthesis still unfinished after this long is treated as failed, so
 *  Retry is offered instead of an endless "Preparing". */
const MAX_PREPARING_MS = 90_000
/** A saved position this close to the end means "finished": start over. */
const RESUME_END_MARGIN_MS = 1_000
/** Synthesis takes ~20s; the bar eases toward (never reaches) 95% over that time so a
 *  first listener sees steady movement rather than a frozen control. */
const PREPARING_EASE_SECONDS = 10
const preparingPercent = (seconds: number) =>
  Math.round(95 * (1 - Math.exp(-seconds / PREPARING_EASE_SECONDS)))
const SPEED_CYCLE = [1, 1.25, 1.5, 2] as const
const BACK_SECONDS = 15

const formatTime = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, "0")}`
}

/**
 * "Listen while driving": nothing about this component touches the network
 * or the DOM's `<audio>` element until the reader presses Play — no
 * autoplay, no eager fetch. From there it owns polling the (possibly still
 * synthesizing) asset, the transcript toggle, and — via the Media Session
 * API when the browser exposes one — the lock-screen/car play-pause-seek
 * controls, so playback survives the tab losing focus.
 *
 * Shared verbatim by the internal and customer-facing surfaces, so every
 * string is an explicit prop rather than this component's own
 * `useTranslation` call — the customer-facing guide may render outside the
 * authenticated app's i18n bundle entirely.
 */
export const EducationAudioPlayer = ({
  fetchAudio,
  retryAudio,
  initialPositionMs = 0,
  onProgress,
  onPlay,
  transcript,
  compactOnMobile = false,
  title,
  labels
}: EducationAudioPlayerProps) => {
  const [playerState, setPlayerState] = useState<PlayerState>("idle")
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [durationMs, setDurationMs] = useState(0)
  const [currentTimeMs, setCurrentTimeMs] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [rateIndex, setRateIndex] = useState(0)
  const [showTranscript, setShowTranscript] = useState(false)
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false)
  const [announcement, setAnnouncement] = useState("")
  const [autoplayBlocked, setAutoplayBlocked] = useState(false)
  const [preparingSeconds, setPreparingSeconds] = useState(0)

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const autoPlayOnReadyRef = useRef(false)
  const attemptIdRef = useRef(0)
  const onProgressRef = useRef(onProgress)
  onProgressRef.current = onProgress

  const reporterRef = useRef(
    createAudioProgressReporter((ms, completed) => onProgressRef.current?.(ms, completed))
  )

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  // A browser can refuse `play()` (autoplay policy, a revoked/expired asset
  // url, a codec it won't decode) — an uncaught rejection here would surface
  // as an unhandled-promise-rejection console error with no UI signal at
  // all. Surfacing the failed state also gives the reader the existing
  // Retry affordance instead of a silently dead Play button.
  const playAudio = useCallback((audio: HTMLAudioElement) => {
    audio.play().catch((error: unknown) => {
      reportUnexpectedFailure(error, { flow: "education-audio.play" })
      setPlayerState("failed")
    })
  }, [])

  useEffect(() => {
    if (playerState !== "preparing") return
    setPreparingSeconds(0)
    const ticker = setInterval(() => setPreparingSeconds((seconds) => seconds + 1), 1_000)
    return () => clearInterval(ticker)
  }, [playerState])

  useEffect(
    () => () => {
      clearPollTimer()
      attemptIdRef.current++
    },
    [clearPollTimer]
  )

  const beginPreparing = useCallback(() => {
    clearPollTimer()
    setPlayerState("preparing")
    setAnnouncement("")
    const attemptId = ++attemptIdRef.current
    const startedAt = Date.now()

    const poll = () => {
      fetchAudio()
        .then((result) => {
          if (attemptId !== attemptIdRef.current) return
          if (result.status === "ready") {
            setAudioUrl(result.url)
            setDurationMs(result.duration_ms)
            setPlayerState("ready")
            setAnnouncement(labels.audioReady)
          } else if (
            result.status === "failed" ||
            Date.now() - startedAt + POLL_INTERVAL_MS > MAX_PREPARING_MS
          ) {
            setPlayerState("failed")
          } else {
            pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS)
          }
        })
        .catch((error: unknown) => {
          if (attemptId !== attemptIdRef.current) return
          reportUnexpectedFailure(error, { flow: "education-audio.fetch" })
          setPlayerState("failed")
        })
    }
    poll()
  }, [fetchAudio, labels.audioReady, clearPollTimer])

  const handleBack15 = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.currentTime = Math.max(0, audio.currentTime - BACK_SECONDS)
    setCurrentTimeMs(audio.currentTime * 1000)
  }, [])

  const handleRestart = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.currentTime = 0
    setCurrentTimeMs(0)
    playAudio(audio)
  }, [playAudio])

  const handlePlayClick = useCallback(() => {
    if (playerState === "idle") {
      autoPlayOnReadyRef.current = true
      beginPreparing()
      return
    }
    if (playerState === "ready") {
      const audio = audioRef.current
      if (!audio) return
      if (audio.paused) {
        setAutoplayBlocked(false)
        playAudio(audio)
      } else {
        audio.pause()
      }
    }
  }, [playerState, beginPreparing, playAudio])

  const handleRetry = useCallback(() => {
    const attempt = retryAudio ? retryAudio() : Promise.resolve()
    attempt
      .then(() => {
        autoPlayOnReadyRef.current = true
        beginPreparing()
      })
      .catch((error: unknown) => {
        reportUnexpectedFailure(error, { flow: "education-audio.retry" })
        setPlayerState("failed")
      })
  }, [retryAudio, beginPreparing])

  const handleCycleSpeed = useCallback(() => {
    setRateIndex((index) => {
      const next = (index + 1) % SPEED_CYCLE.length
      const audio = audioRef.current
      if (audio) audio.playbackRate = SPEED_CYCLE[next]
      return next
    })
  }, [])

  const handleSeek = useCallback(
    (percent: number) => {
      const audio = audioRef.current
      if (!audio || !durationMs) return
      const ms = (percent / 100) * durationMs
      audio.currentTime = ms / 1000
      setCurrentTimeMs(ms)
    },
    [durationMs]
  )

  const handleToggleTranscript = useCallback(() => setShowTranscript((value) => !value), [])

  useEffect(() => {
    if (playerState !== "ready") return
    const audio = audioRef.current
    if (!audio) return
    audio.playbackRate = SPEED_CYCLE[rateIndex]
    const finished = durationMs > 0 && initialPositionMs >= durationMs - RESUME_END_MARGIN_MS
    if (initialPositionMs > 0 && !finished) {
      audio.currentTime = initialPositionMs / 1000
    }
    if (autoPlayOnReadyRef.current) {
      autoPlayOnReadyRef.current = false
      // A real user gesture (the original Play click) started this, but by
      // the time synthesis finishes and this effect fires, that gesture is
      // several ticks removed — a browser's autoplay policy can still
      // refuse it. That refusal means "press Play yourself", not "synthesis
      // failed", so it gets its own state rather than falling into `failed`
      // (see `EducationAudioPlayerLabels.autoplayBlocked`). A direct click
      // on the Play button itself (`handlePlayClick`'s ready branch) is a
      // real gesture in the same call stack and isn't routed through here.
      audio.play().catch(() => setAutoplayBlocked(true))
    }
    // Runs once per newly-ready asset only — `rateIndex` changing (the
    // speed cycle button) must not re-seek or re-trigger autoplay.
  }, [playerState, audioUrl])

  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return
    const mediaSession = navigator.mediaSession
    mediaSession.setActionHandler("play", () => {
      const audio = audioRef.current
      if (audio) playAudio(audio)
    })
    mediaSession.setActionHandler("pause", () => audioRef.current?.pause())
    mediaSession.setActionHandler("seekbackward", () => handleBack15())
    return () => {
      mediaSession.setActionHandler("play", null)
      mediaSession.setActionHandler("pause", null)
      mediaSession.setActionHandler("seekbackward", null)
    }
  }, [handleBack15, playAudio])

  const handleTimeUpdate = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    const ms = event.currentTarget.currentTime * 1000
    setCurrentTimeMs(ms)
    reporterRef.current.report(ms)
  }

  const handleLoadedMetadata = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    const { duration } = event.currentTarget
    if (Number.isFinite(duration) && duration > 0) {
      setDurationMs(duration * 1000)
    }
  }

  const setMediaSessionState = (state: MediaSessionPlaybackState) => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return
    navigator.mediaSession.playbackState = state
    if (state === "playing" && title && typeof MediaMetadata !== "undefined") {
      navigator.mediaSession.metadata = new MediaMetadata({ title })
    }
  }

  const handlePlay = () => {
    setIsPlaying(true)
    setHasPlayedOnce(true)
    setAutoplayBlocked(false)
    setMediaSessionState("playing")
    onPlay?.()
  }

  const handlePause = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    setIsPlaying(false)
    setMediaSessionState("paused")
    reporterRef.current.flush(event.currentTarget.currentTime * 1000, false)
  }

  const handleEnded = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    setIsPlaying(false)
    setMediaSessionState("paused")
    reporterRef.current.flush(event.currentTarget.currentTime * 1000, true)
  }

  const progressPercent = durationMs ? (currentTimeMs / durationMs) * 100 : 0
  const isReady = playerState === "ready"

  return (
    <div className="education-audio-player">
      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>

      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="none"
          className="hidden"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
        />
      )}

      {playerState === "preparing" && (
        <div className="flex items-center gap-3 text-sm text-gray-700" role="status">
          <LoadingOutlined
            className="text-2xl text-[#2A7D8C]"
            spin
            data-testid="education-audio-preparing-spinner"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span>{labels.preparing}</span>
              <span className="tabular-nums text-xs text-gray-500" aria-hidden="true">
                {labels.elapsedSeconds(preparingSeconds)}
              </span>
            </div>
            <div
              role="progressbar"
              aria-label={labels.preparing}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={preparingPercent(preparingSeconds)}
              className="mt-1 h-1.5 w-full overflow-hidden rounded bg-gray-200"
            >
              <div
                className="h-full rounded bg-[#2A7D8C] transition-[width] duration-1000 ease-linear"
                style={{ width: `${preparingPercent(preparingSeconds)}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {playerState === "failed" && (
        <div className="flex items-center gap-2 text-sm text-red-600" role="status">
          <ExclamationCircleOutlined />
          <span>{labels.failed}</span>
          <Button size="small" aria-label={labels.retry} icon={<RedoOutlined />} onClick={handleRetry}>
            {labels.retry}
          </Button>
        </div>
      )}

      {playerState === "ready" && autoplayBlocked && !isPlaying && (
        <div className="mb-2 flex items-center gap-2 text-sm text-blue-700" role="status">
          <PlayCircleOutlined />
          <span>{labels.autoplayBlocked}</span>
        </div>
      )}

      {(playerState === "idle" || playerState === "ready") && (
        <div className="flex items-center gap-3">
          <Button
            shape="circle"
            type="text"
            aria-label={isPlaying ? labels.pause : labels.play}
            icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={handlePlayClick}
          />
          <Button
            type="text"
            aria-label={labels.back15}
            icon={<FastBackwardOutlined />}
            disabled={!isReady}
            onClick={handleBack15}
          >
            {labels.back15Short}
          </Button>
          <Button
            type="text"
            aria-label={labels.restart}
            icon={<UndoOutlined />}
            disabled={!isReady}
            onClick={handleRestart}
          />
          <Button
            type="text"
            aria-label={`${labels.speed}: ${SPEED_CYCLE[rateIndex]}x`}
            disabled={!isReady}
            onClick={handleCycleSpeed}
          >
            {SPEED_CYCLE[rateIndex]}x
          </Button>
          <Slider
            className="flex-1"
            min={0}
            max={100}
            step={0.1}
            value={progressPercent}
            onChange={handleSeek}
            disabled={!isReady}
            tooltip={{ open: false }}
            ariaLabelForHandle={labels.seek}
          />
          <span className="whitespace-nowrap text-xs text-gray-500">
            {formatTime(currentTimeMs)} / {formatTime(durationMs)}
          </span>
        </div>
      )}

      <div className="mt-3">
        <Button
          type="link"
          icon={<FileTextOutlined />}
          aria-expanded={showTranscript}
          aria-label={showTranscript ? labels.transcriptHide : labels.transcriptShow}
          onClick={handleToggleTranscript}
        >
          {showTranscript ? labels.transcriptHide : labels.transcriptShow}
        </Button>
        {showTranscript && <div className="mt-2 text-sm text-gray-700">{transcript}</div>}
      </div>

      {compactOnMobile && hasPlayedOnce && (
        <div
          className={cn(
            "sticky bottom-[env(safe-area-inset-bottom)] z-10 flex items-center gap-3",
            "border-t border-gray-200 bg-white px-4 py-2 md:hidden"
          )}
        >
          <Button
            shape="circle"
            type="text"
            aria-label={isPlaying ? labels.pause : labels.play}
            icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={handlePlayClick}
          />
          <div className="h-1 flex-1 overflow-hidden rounded bg-gray-200">
            <div
              className="h-full bg-blue-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
